/**
 * Annotation Batches Page
 * Manage annotation batches with list view and actions
 */

import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Plus, AlertCircle, Filter, Database, BarChart3, FolderOpen } from 'lucide-react';
import {
  BatchCard,
  useBatches,
  useActivateBatch,
  useArchiveBatch,
  useRestoreBatch,
  useDeleteBatch,
} from '@/features/annotations';
import type { AnnotationBatch, BatchStatus } from '@/types/annotation';

// Sub-navigation tabs
const TABS = [
  { path: '/annotation/batches', label: '批次管理', icon: FolderOpen },
  { path: '/annotation/export', label: '数据导出', icon: Database },
  { path: '/annotation/stats', label: '统计报表', icon: BarChart3 },
];

const STATUS_OPTIONS: { value: BatchStatus | 'all'; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '进行中' },
  { value: 'completed', label: '已完成' },
  { value: 'archived', label: '已归档' },
];

export function AnnotationBatchesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<BatchStatus | 'all'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<AnnotationBatch | null>(null);

  // Queries and mutations
  const { data: batches = [], isLoading, error } = useBatches(
    statusFilter === 'all' ? undefined : statusFilter
  );
  const activateMutation = useActivateBatch();
  const archiveMutation = useArchiveBatch();
  const restoreMutation = useRestoreBatch();
  const deleteMutation = useDeleteBatch();

  const handleActivate = async (batch: AnnotationBatch) => {
    try {
      await activateMutation.mutateAsync(batch.id);
    } catch (error) {
      console.error('Failed to activate batch:', error);
      alert('激活失败: ' + (error as Error).message);
    }
  };

  const handleArchive = async (batch: AnnotationBatch) => {
    try {
      await archiveMutation.mutateAsync(batch.id);
    } catch (error) {
      console.error('Failed to archive batch:', error);
    }
  };

  const handleRestore = async (batch: AnnotationBatch) => {
    try {
      await restoreMutation.mutateAsync(batch.id);
    } catch (error) {
      console.error('Failed to restore batch:', error);
      alert('恢复失败: ' + (error as Error).message);
    }
  };

  const handleDelete = (batch: AnnotationBatch) => {
    setDeleteConfirm(batch);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete batch:', error);
    }
  };

  const handleViewTasks = (batch: AnnotationBatch) => {
    navigate(`/annotation/workbench?batch=${batch.id}`);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">数据标注</h1>
          <Button variant="primary" onClick={() => navigate('/annotation/export')}>
            <Plus className="w-4 h-4 mr-2" />
            创建批次
          </Button>
        </div>

        {/* Sub-navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`
                  flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Page Description */}
      <div className="mb-6">
        <p className="text-gray-600">
          管理标注任务批次，追踪标注进度。共 {batches.length} 个批次。
        </p>
      </div>

      {/* Filter Bar */}
      <div className="mb-6 flex items-center gap-2">
        <Filter className="w-4 h-4 text-gray-500" />
        {STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setStatusFilter(option.value)}
            className={`
              px-4 py-2 rounded-lg border transition-colors text-sm
              ${
                statusFilter === option.value
                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-800 font-semibold mb-1">加载失败</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500">加载批次...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && batches.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="text-gray-400 mb-4">
            <Plus className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无批次</h3>
          <p className="text-gray-500 mb-4">开始创建第一个标注批次</p>
          <Button variant="primary" onClick={() => navigate('/annotation/export')}>
            <Plus className="w-4 h-4 mr-2" />
            创建批次
          </Button>
        </div>
      )}

      {/* Batches Grid */}
      {!isLoading && !error && batches.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {batches.map((batch) => (
            <BatchCard
              key={batch.id}
              batch={batch}
              onActivate={handleActivate}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onDelete={handleDelete}
              onViewTasks={handleViewTasks}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">删除批次</h3>
            <p className="text-gray-600 mb-4">
              确定要删除批次 <strong>{deleteConfirm.title}</strong> 吗？
              <br />
              <span className="text-red-600 text-sm">此操作将删除该批次下的所有任务，无法恢复。</span>
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                取消
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                确认删除
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
